import { useEffect, useState } from "react";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useEffectiveUserId } from "@/hooks/useEffectiveUserId";

/**
 * What to call the person on screen.
 *
 * Extracted from Header rather than written twice. The Assistant's launchpad needed the same
 * name the Header already shows, and a second copy of this would drift — one screen calling
 * someone by their profile name while another falls back to an email prefix is the kind of
 * difference nobody reports and everybody notices.
 *
 * Keyed on the EFFECTIVE user, so it follows "View as user" exactly as the Header does.
 */
export function useDisplayName(): string {
  const { user } = useAuth();
  const effectiveUserId = useEffectiveUserId();
  const [name, setName] = useState<string | null>(null);

  useEffect(() => {
    if (!effectiveUserId) {
      setName(null);
      return;
    }
    let cancelled = false;
    supabase
      .from("profiles")
      .select("display_name")
      .eq("user_id", effectiveUserId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setName(data?.display_name ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveUserId]);

  // Same fallback chain the Header has always used.
  return name?.trim() || user?.email?.split("@")[0] || "there";
}
